import { useNow, useTimeZone } from '@/app/preferences';
import { formatClockZone, formatDateTime, formatRelative, sameDay } from '@/domain/time';

/**
 * A timestamp in the viewer's chosen zone. The full date and zone are always in the title
 * so a short "10:45 UTC" can be expanded on hover, and the machine-readable instant is on
 * the <time> element.
 */
export function Timestamp({
  iso,
  mode = 'auto',
  withSeconds = false,
  className,
}: {
  iso: string;
  /** auto: clock only when today, full date otherwise. */
  mode?: 'auto' | 'clock' | 'full' | 'relative';
  withSeconds?: boolean;
  className?: string;
}) {
  const tz = useTimeZone();
  const now = useNow();
  const full = formatDateTime(iso, tz, true);
  let text: string;
  if (mode === 'relative') text = formatRelative(iso, now);
  else if (mode === 'clock') text = formatClockZone(iso, tz, withSeconds);
  else if (mode === 'full') text = formatDateTime(iso, tz, withSeconds);
  else
    text = sameDay(iso, new Date(now).toISOString(), tz)
      ? formatClockZone(iso, tz, withSeconds)
      : formatDateTime(iso, tz, withSeconds);
  return (
    <time
      dateTime={iso}
      title={full}
      className={['tabular whitespace-nowrap', className].filter(Boolean).join(' ')}
    >
      {text}
    </time>
  );
}

export function RelativeTime({ iso, className }: { iso: string; className?: string }) {
  return <Timestamp iso={iso} mode="relative" {...(className ? { className } : {})} />;
}
